import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GenericResModel } from 'countable@model';
import { BehaviorSubject, catchError, debounceTime, map, Observable, Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { IntegrationSourceInterface } from '../../../model/eng/integration-source.interface';
import { FirmUpdateModel } from '../../../model/firm/firm-update.model';
import { FirmDetailsSource } from '../../../model/firm/onboarding/firm-source.model';
import { OnboardedClientModel } from '../../../model/firm/onboarding/onboarded-client.model';
import { ClientSaveOnboardingModel } from '../../../model/firm/onboarding/save-client-onboarding.model';
import { StepPath } from './getting-started.contants';
import { FirmTeamOnboardingModel } from 'src/app/model/firm/onboarding/teams.model';
import { FirmUserData } from 'src/app/model/firm/onboarding/firm-user.model';

@Injectable({
  providedIn: "root",
})
export class GettingStartedService {
  private static readonly URI_FIRM_UPDATE = environment.apiV1 + "/onboarding/firm";
  private static readonly URI_TEAM = environment.apiV1 + "/onboarding/team";
  private static readonly URI_OAUTH_STATUS = environment.apiV1 + "/onboarding/oauth-status";

  private stepValidity = new Map<StepPath, BehaviorSubject<boolean>>();
  private updateSubjects = new Map<StepPath, Subject<boolean>>();

  firmOnboardingFormData$: Subject<any> = new Subject<any>();
  teamsOnboardingCallApi$: Subject<boolean> = new Subject<boolean>();
  clientOnboardingCallApi$: Subject<boolean> = new Subject<boolean>();
  gotoStep3$: Subject<boolean> = new Subject<boolean>();
  gotoStep4$: Subject<boolean> = new Subject<boolean>();
  notifications$: Subject<any> = new Subject<any>();
  dashboardDrawerStatus$: Subject<boolean> = new Subject<boolean>();
  onboardingDrawerStatus$: Subject<boolean> = new Subject<boolean>();

  constructor(private http: HttpClient) {
    Object.values(StepPath).forEach((step) => {
      const validity$ = new BehaviorSubject<boolean>(false);
      const update$ = new Subject<boolean>();

      this.stepValidity.set(step, validity$);
      this.updateSubjects.set(step, update$);

      update$.pipe(debounceTime(300)).subscribe((value) => {
        validity$.next(value);
      });
    });
  }

  setStepValidity(step: StepPath, isValid: boolean) {
    this.updateSubjects.get(step)?.next(isValid);
  }

  getStepValidity(step: StepPath) {
    return this.stepValidity.get(step)?.asObservable();
  }

  getFirmOnboardingStatus(): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(environment.apiV1 + "/onboarding/status");
  }

  getOauthStatus(): Observable<GenericResModel<IntegrationSourceInterface>> {
    return this.http.get<GenericResModel<IntegrationSourceInterface>>(GettingStartedService.URI_OAUTH_STATUS);
  }

  getTeam(): Observable<any> {
    return this.http.get(GettingStartedService.URI_TEAM);
  }

  saveFirmSetup(data: FirmUpdateModel): Observable<any> {
    return this.http.put(GettingStartedService.URI_FIRM_UPDATE, data);
  }

  getFirmUser(): Observable<FirmUserData> {
    return this.http.get<GenericResModel<FirmUserData>>(GettingStartedService.URI_FIRM_UPDATE).pipe(
      map((response) => {
        if (response.status === 200) {
          return response.data;
        }
        throw new Error("Invalid response status");
      }),
      catchError((error) => {
        console.error("Error fetching OAuth firm details:", error);
        throw error;
      })
    );
  }

  teamSetup(data: any): Observable<any> {
    return this.http.post(environment.apiV1 + "/onboarding/team", data);
  }

  getOauthFirm(): Observable<FirmDetailsSource> {
    return this.http.get<GenericResModel<FirmDetailsSource>>(environment.apiV1 + "/onboarding/oauth-firm").pipe(
      map((response) => {
        if (response.status === 200) {
          return response.data;
        }
        throw new Error("Invalid response status");
      }),
      catchError((error) => {
        console.error("Error fetching OAuth firm details:", error);
        throw error;
      })
    );
  }

  getOauthTeam(): Observable<FirmTeamOnboardingModel[]> {
    return this.http.get<GenericResModel<FirmTeamOnboardingModel[]>>(environment.apiV1 + "/onboarding/oauth-team").pipe(
      map((response) => {
        if (response.status === 200) {
          return response.data ? response.data : [];
        }
        throw new Error("Invalid response status");
      }),
      catchError((error) => {
        console.error("Error fetching OAuth firm details:", error);
        throw error;
      })
    );
  }

  saveClientOnboarding(data: ClientSaveOnboardingModel): Observable<GenericResModel<any>> {
    return this.http.post<GenericResModel<any>>(environment.apiV1 + "/onboarding/client", data);
  }

  getOauthClient(): Observable<ClientSaveOnboardingModel[]> {
    return this.http
      .get<GenericResModel<ClientSaveOnboardingModel[]>>(environment.apiV1 + "/onboarding/oauth-client")
      .pipe(
        map((response) => {
          if (response.status === 200) {
            return response.data ? response.data : [];
          }
          throw new Error("Invalid response status");
        }),
        catchError((error) => {
          console.error("Error fetching OAuth firm details:", error);
          throw error;
        })
      );
  }

  getClientInfo(): Observable<GenericResModel<OnboardedClientModel>> {
    return this.http.get<GenericResModel<OnboardedClientModel>>(environment.apiV1 + "/onboarding/client");
  }

  completeOnboarding(): Observable<any> {
    return this.http.put(environment.apiV1 + '/onboarding/eng', {});
  }

  completeComponentsStep(): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(environment.apiV1 + '/onboarding/component', {});
  }
}
